import {
    asTransportFailure,
    defaultRetryPolicy,
    FetchRequest,
    fetchWithRetry,
    NetworkTransportError,
    RequestConstructionError,
} from '~/backend/network-retry';
import { isAbortError, RetryNeededError } from '~/backend/retryOn202';
import { ConnectorContext } from './connector-context';

const baseUrl =
    import.meta.env.VITE_CONNECTOR_BASE_URL ?? window.location.origin;

export type ErrorWrapper<TError> =
    | TError
    | { status: 'unknown'; payload: string };

export type ConnectorFetcherOptions<
    TBody,
    THeaders,
    TQueryParams,
    TPathParams,
> = {
    url: string;
    method: string;
    body?: TBody;
    headers?: THeaders;
    queryParams?: TQueryParams;
    pathParams?: TPathParams;
    signal?: AbortSignal;
} & ConnectorContext['fetcherOptions'];

export async function connectorFetch<
    TData,
    TError,
    TBody extends {} | FormData | undefined | null,
    THeaders extends {},
    TQueryParams extends {},
    TPathParams extends {},
>({
    url,
    method,
    body,
    headers,
    pathParams,
    queryParams,
    signal,
    getAuthHeaders,
    retryPolicy,
}: ConnectorFetcherOptions<
    TBody,
    THeaders,
    TQueryParams,
    TPathParams
>): Promise<TData> {
    try {
        const requestUrl = `${baseUrl}${resolveUrl(
            url,
            queryParams,
            pathParams
        )}`;
        const requestMethod = method.toUpperCase();

        // Rebuilt per attempt so a retry carries a freshly resolved
        // authorization header — a transfer can outlive the token that
        // started it. The body (FormData or source object) is reused; the
        // browser serializes it anew on every send.
        const buildRequest = async (): Promise<FetchRequest> => {
            const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
            const requestHeaders: HeadersInit = {
                'Content-Type': 'application/json',
                ...authHeaders,
                ...headers,
            };

            /**
             * As the fetch API is being used, when multipart/form-data is
             * specified the Content-Type header must be deleted so that the
             * browser can set the correct boundary.
             * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
             */
            if (
                requestHeaders['Content-Type']
                    .toLowerCase()
                    .includes('multipart/form-data')
            ) {
                delete requestHeaders['Content-Type'];
            }

            return {
                url: requestUrl,
                init: {
                    signal,
                    method: requestMethod,
                    body: body
                        ? body instanceof FormData
                            ? body
                            : JSON.stringify(body)
                        : undefined,
                    headers: requestHeaders,
                },
            };
        };

        let response: Response;
        try {
            response = await fetchWithRetry(buildRequest, {
                signal,
                // The URL template, never the resolved URL: legacy paths
                // carry patient ids and DICOM UIDs.
                label: `${requestMethod} ${url}`,
                retryPolicy:
                    retryPolicy ?? defaultRetryPolicy(requestMethod, headers),
            });
        } catch (e) {
            // A transport failure is typed apart from the HTTP errors below
            // so callers see exactly which failures a reconnect could fix;
            // anything else (a cancellation, a failed token request) passes
            // to the legacy handler below.
            throw asTransportFailure(e);
        }

        if (response.status === 202) {
            // 202 is considered a need to be retried
            let error: RetryNeededError;
            error = new RetryNeededError();
            throw error;
        }
        if (!response.ok) {
            let error: ErrorWrapper<TError>;
            try {
                error = await response.json();
            } catch (e) {
                error = {
                    status: 'unknown' as const,
                    payload:
                        e instanceof Error
                            ? `Unexpected error (${e.message})`
                            : 'Unexpected error',
                };
            }

            throw error;
        }

        if (response.headers.get('content-type')?.includes('json')) {
            return await response.json();
        } else {
            // if it is not a json response, assume it is a blob and cast it to TData
            return (await response.blob()) as unknown as TData;
        }
    } catch (e) {
        if (
            e instanceof RetryNeededError ||
            e instanceof NetworkTransportError ||
            e instanceof RequestConstructionError ||
            isAbortError(e)
        ) {
            throw e;
        } else {
            let errorObject: Error = {
                name: 'unknown' as const,
                message:
                    e instanceof Error
                        ? `Network error (${e.message})`
                        : 'Network error',
                stack: e as string,
            };
            throw errorObject;
        }
    }
}

const resolveUrl = (
    url: string,
    queryParams: Record<string, string> = {},
    pathParams: Record<string, string> = {}
) => {
    let query = new URLSearchParams(queryParams).toString();
    if (query) query = `?${query}`;
    return (
        url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query
    );
};
